'use client';

import { useState, useEffect, useRef } from 'react';
import { MessageSquareWarning } from 'lucide-react';
import { Button } from '@/lib/components/ui/button';
import { FeedbackModal } from './FeedbackModal';
import { useAppConfig } from '@/lib/context/AppConfigContext';

export function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const clickCountRef = useRef(0);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const singleClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use centralized app config context
  const { isEnabled, isLoading } = useAppConfig();

  // Check if issue reporting is enabled
  // Must check both module (module/issues_tracking_status) and feature (feature/issue_reporting_status)
  const moduleEnabled = isEnabled('module/issues_tracking_status');
  const featureEnabled = isEnabled('feature/issue_reporting_status');
  const isButtonEnabled = moduleEnabled && featureEnabled;

  const handleClick = () => {
    // Clear any pending single click
    if (singleClickTimeoutRef.current) {
      clearTimeout(singleClickTimeoutRef.current);
    }

    // Delay single click to allow double-click detection
    singleClickTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, 250);
  };

  const handleDoubleClick = () => {
    // Cancel the pending single click
    if (singleClickTimeoutRef.current) {
      clearTimeout(singleClickTimeoutRef.current);
      singleClickTimeoutRef.current = null;
    }
    setIsHidden(true);
  };

  // Track clicks anywhere to show icon after 4 consecutive clicks
  useEffect(() => {
    if (!isHidden) return;

    const handleGlobalClick = () => {
      clickCountRef.current += 1;

      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }

      if (clickCountRef.current >= 4) {
        setIsHidden(false);
        clickCountRef.current = 0;
      } else {
        clickTimeoutRef.current = setTimeout(() => {
          clickCountRef.current = 0;
        }, 1000);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, [isHidden]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (singleClickTimeoutRef.current) {
        clearTimeout(singleClickTimeoutRef.current);
      }
    };
  }, []);

  // Don't render if loading, disabled, or manually hidden
  if (isLoading || !isButtonEnabled || isHidden) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className='fixed bottom-4 right-4 h-8 w-8 rounded-full shadow-md opacity-70 hover:opacity-100 transition-opacity pointer-events-auto p-0'
        style={{ zIndex: 2147483647 }}
        variant='outline'
        size='xs'
        title='Report an issue (double-click to hide)'
      >
        <MessageSquareWarning className='h-4 w-4' />
      </Button>
      <FeedbackModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
